/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';

      const disableAutoplayMobile = this.dataset.disableAutoplayMobile === 'true';
      const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
      if (disableAutoplayMobile && isMobileViewport) {
        this.autoplay = false;
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');
      if (!this.player) return;

      if (!this.autoplay) {
        this.player.removeAttribute('autoplay');
      }

      // iOS Safari is strict about inline autoplay requirements.
      this.player.playsInline = true;
      this.player.setAttribute('playsinline', '');
      this.player.setAttribute('webkit-playsinline', '');

      if (this.autoplay || this.background) {
        this.player.defaultMuted = true;
        this.player.muted = true;
        this.player.setAttribute('muted', '');
      }

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      this.player.addEventListener('play', () => {
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.markPlayed();
      } else {
        this.player.addEventListener('play', () => {
          this.markPlayed();
        });

        this.addObserver(this.player);

        const deferredMedia = this.closest('deferred-media');
        // Mute the video if a click didn't trigger its load
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
          this.player.defaultMuted = true;
          this.player.setAttribute('muted', '');
        }

        // Mobile browsers can block click-triggered playback when the media starts with audio.
        if (deferredMedia?.loadTrigger === 'click' && !this.autoplay && window.matchMedia('(max-width: 767px)').matches) {
          this.player.muted = true;
          this.player.defaultMuted = true;
          this.player.setAttribute('muted', '');
        }

        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.markPlayed();
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth && !this.closest('.video-section--slider')) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        const playResult = this.player.play();
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch(() => {
            // Keep poster/fallback visible when autoplay is blocked on mobile.
          });
        }
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        }, { rootMargin: '0px 0px 200px 0px' });
      });

      observer.observe(el);
    }

    /**
     * Marks this media as played at section/slide level.
     */
    markPlayed() {
      const slide = this.closest('.video-section__slide');
      if (slide) {
        slide.classList.add('video-section__slide--played');
      }

      this.closest('.video-section')?.classList.add('video-section--played');
    }
  }

  customElements.define('video-component', Video);
}

if (!customElements.get('video-section-slider')) {
  class VideoSectionSlider extends HTMLElement {
    connectedCallback() {
      this.carousel = this.querySelector('carousel-slider[data-video-slider="true"]');
      this.slider = this.querySelector('.video-section__slider');

      if (!this.carousel || !this.slider) return;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMediaPlay = this.handleMediaPlay.bind(this);

      this.slider.addEventListener('scroll', this.handleScroll);
      window.addEventListener('resize', this.handleResize);
      this.addEventListener('on:media:play', this.handleMediaPlay);

      this.mutationObserver = new MutationObserver(() => {
        this.syncPlayback();
      });
      this.mutationObserver.observe(this, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['loaded']
      });

      this.syncPlayback();
      this.disableDesktopDrag();
    }

    disconnectedCallback() {
      if (this.slider) {
        this.slider.removeEventListener('scroll', this.handleScroll);
      }

      window.removeEventListener('resize', this.handleResize);
      this.removeEventListener('on:media:play', this.handleMediaPlay);

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    }

    handleScroll() {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.snapToNearestSlide();
        this.syncPlayback();
      }, 120);
    }

    handleResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.disableDesktopDrag();
        this.syncPlayback();
      }, 120);
    }

    syncPlayback() {
      const slides = this.querySelectorAll('.video-section__slide');
      if (!slides.length) return;
      const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;

      const activeIndex = this.getActiveIndex(slides);
      const activeSlide = slides[activeIndex];

      // In slider mode, load media on-demand for the active slide only.
      if (activeSlide) {
        activeSlide.querySelectorAll('deferred-media[data-slider-autoplay="true"]:not([loaded])').forEach((deferredMedia) => {
          const autoplayOnMobile = deferredMedia.dataset.sliderAutoplayMobile !== 'false';
          if (isMobileViewport && !autoplayOnMobile) return;

          if (typeof deferredMedia.loadContent === 'function') {
            deferredMedia.loadContent(false, false, 'slider');
          }
        });
      }

      slides.forEach((slide, index) => {
        this.normalizeSlideMedia(slide);

        slide.querySelectorAll('video-component').forEach((component) => {
          if (typeof component.pause !== 'function' || typeof component.play !== 'function') return;

          if (index !== activeIndex) {
            if (component.autoplay) component.pause();
          } else if (component.autoplay) {
            component.play();
          }
        });
      });
    }

    /**
     * Normalizes slide media element sizing to avoid partial-width controls.
     * @param {Element} slide - Slide element.
     */
    normalizeSlideMedia(slide) {
      slide.querySelectorAll('video-component').forEach((component) => {
        component.style.width = '100%';
        component.style.height = '100%';
        component.style.left = '0';
        component.style.top = '0';
        component.style.transform = 'none';

        const media = component.querySelector('video, iframe');
        if (!media) return;

        media.style.width = '100%';
        media.style.height = '100%';
        media.style.left = '0';
        media.style.top = '0';
      });
    }

    /**
     * Pauses a media component if it starts playing on a non-active slide.
     * @param {Event} evt - Media play custom event.
     */
    handleMediaPlay(evt) {
      const sourceComponent = evt.target.closest('video-component');
      if (!sourceComponent) return;

      const sourceSlide = sourceComponent.closest('.video-section__slide');
      if (!sourceSlide) return;

      const slides = this.querySelectorAll('.video-section__slide');
      const activeIndex = this.getActiveIndex(slides);
      const sourceIndex = Array.from(slides).indexOf(sourceSlide);

      if (sourceIndex !== activeIndex && sourceComponent.autoplay && typeof sourceComponent.pause === 'function') {
        const sliderRect = this.slider.getBoundingClientRect();
        const sourceRect = sourceSlide.getBoundingClientRect();
        const overlapWidth = Math.max(0, Math.min(sourceRect.right, sliderRect.right) - Math.max(sourceRect.left, sliderRect.left));
        const overlapRatio = sourceRect.width > 0 ? overlapWidth / sourceRect.width : 0;

        // Only pause if the playing slide is mostly out of view.
        if (overlapRatio < 0.35) {
          sourceComponent.pause();
        }
      }
    }

    /**
     * Gets current active slide index.
     * @param {NodeListOf<Element>} slides - Slider slides.
     * @returns {number}
     */
    getActiveIndex(slides) {
      const sliderCenter = this.slider.scrollLeft + this.slider.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const center = slide.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(center - sliderCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    /**
     * Snaps slider to nearest full slide to avoid half-slide resting positions.
     */
    snapToNearestSlide() {
      if (!this.slider) return;

      const slides = this.querySelectorAll('.video-section__slide');
      if (!slides.length) return;

      const activeIndex = this.getActiveIndex(slides);
      const targetLeft = slides[activeIndex].offsetLeft;

      if (Math.abs(this.slider.scrollLeft - targetLeft) <= 1) return;

      this.slider.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }

    /**
     * Disables mouse drag behavior on desktop/pointer-fine devices for this slider.
     */
    disableDesktopDrag() {
      if (!this.slider || !window.matchMedia('(pointer: fine)').matches) return;

      // carousel-slider adds these handlers; remove them for this specific instance.
      this.slider.removeEventListener('mousedown', this.carousel?.mousedownHandler);
      this.slider.removeEventListener('mouseup', this.carousel?.mouseupHandler);
      this.slider.removeEventListener('mouseleave', this.carousel?.mouseupHandler);
      this.slider.removeEventListener('mousemove', this.carousel?.mousemoveHandler);
      this.slider.classList.remove('is-grabbable', 'is-grabbing', 'is-dragging');
    }
  }

  customElements.define('video-section-slider', VideoSectionSlider);
}
